import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, LogOut, WifiOff, CloudUpload, KeyRound } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { getPendingCount } from "../../lib/offlineQueue";
import { initOfflineSync } from "../../lib/offlineSync";
import { ChangePasswordModal } from "../../components/ChangePasswordModal";
import { toast } from "sonner";

export default function KioskHome() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isOnline = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(getPendingCount());
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  useEffect(() => {
    const refresh = () => setPendingCount(getPendingCount());
    refresh();
    const interval = setInterval(refresh, 3000);

    initOfflineSync((result) => {
      refresh();
      if (result.ok) {
        const flagged = result.visit?.status === "blocked" || result.visit?.list_match?.matched;
        if (flagged) {
          toast.error(`Synced ${result.job.form.visitor_name} — SECURITY ALERT: matched watchlist/blacklist. Notify admin.`, { duration: 10000 });
        } else {
          toast.success(`Synced offline check-in for ${result.job.form.visitor_name}`);
        }
      }
    });

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#eef1fb] text-brand-navy flex flex-col" data-testid="kiosk-home-page">
      <header className="flex items-center justify-between p-6 border-b border-brand-navy/10">
        <div className="flex items-center gap-3">
          <img src="/brand/radish-logo.png" alt="Radish Technologies" className="h-7 w-auto" data-testid="kiosk-brand-logo" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan border-l border-brand-navy/10 pl-3">
            Gate Mode · {user?.name}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {!isOnline && (
            <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-amber-600 bg-amber-50 border border-amber-300 px-3 py-1.5 rounded-full" data-testid="kiosk-offline-indicator">
              <WifiOff size={13} /> Offline
            </span>
          )}
          {pendingCount > 0 && (
            <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-brand-navy bg-brand-cyan/20 border border-brand-cyan/40 px-3 py-1.5 rounded-full" data-testid="kiosk-pending-sync-indicator">
              <CloudUpload size={13} /> {pendingCount} Pending Sync
            </span>
          )}
          <button
            onClick={() => setPasswordModalOpen(true)}
            data-testid="kiosk-change-password-button"
            className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-400 hover:text-brand-cyan"
          >
            <KeyRound size={13} /> Password
          </button>
          <button
            onClick={() => {
              logout();
              navigate("/login");
            }}
            data-testid="kiosk-logout-button"
            className="text-xs uppercase tracking-widest text-slate-400 hover:text-red-600"
          >
            Exit Kiosk
          </button>
        </div>
      </header>
      <main className="flex-1 flex flex-col items-center justify-center gap-8 p-8">
        <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-center text-brand-navy">
          Welcome
          <br />
          Check In
        </h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full max-w-2xl">
          <button
            data-testid="kiosk-checkin-button"
            onClick={() => navigate("/kiosk/checkin")}
            className="h-40 border border-slate-200 bg-white shadow-sm hover:bg-brand-navy hover:text-white hover:border-brand-navy transition-colors flex flex-col items-center justify-center gap-3 rounded-lg"
          >
            <LogIn size={40} />
            <span className="text-lg font-bold uppercase tracking-wide">Check In Visitor</span>
          </button>
          <button
            data-testid="kiosk-checkout-button"
            onClick={() => navigate("/kiosk/checkout")}
            className="h-40 border border-slate-200 bg-white shadow-sm hover:bg-brand-navy hover:text-white hover:border-brand-navy transition-colors flex flex-col items-center justify-center gap-3 rounded-lg"
          >
            <LogOut size={40} />
            <span className="text-lg font-bold uppercase tracking-wide">Check Out Visitor</span>
          </button>
        </div>
      </main>
      <ChangePasswordModal open={passwordModalOpen} onOpenChange={setPasswordModalOpen} hasPassword={user?.has_password !== false} />
    </div>
  );
}
